import React, { useState, useEffect } from 'react';
import {
  FileText, Search, RefreshCw, Eye, X, CheckCircle, XCircle, AlertCircle,
  Clock, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download,
  Phone, Mail, Hash, MessageSquare, User, Calendar, Filter
} from 'lucide-react';
import dayjs from 'dayjs';
import { formatIndianNumber } from '../../../utils/numberFormat';
import { reportsAPI } from '../../../services/api';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
const inputClass = "w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors";
const inputBorder = { border: '1px solid rgba(99, 102, 241, 0.15)' };
const labelClass = "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5";

const toDisplayDate = (isoDate) => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const ReportsDetailed = () => {
  const [fromDate, setFromDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [mobileNumber, setMobileNumber] = useState('');
  const [headerFilter, setHeaderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [messageIdFilter, setMessageIdFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await authFetch(getApiEndpoint('/migration/summary-fetch/users'));
        if (response.ok) {
          const result = await response.json();
          setUsers(result.data || []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  const showToast = (type, msg) => {
    setToast({ show: true, type, message: msg });
    setTimeout(() => setToast({ show: false, type: '', message: '' }), 4000);
  };

  const fetchData = async (p = 1, ps = 10) => {
    if (!fromDate || !toDate) {
      showToast('error', 'Please select From Date and To Date');
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: p,
        page_size: ps,
        report_type: 'detailed',
        start_date: fromDate,
        end_date: toDate,
      };
      if (mobileNumber) params.mobile_number = mobileNumber;
      if (headerFilter) params.header = headerFilter;
      if (statusFilter) params.status = statusFilter;
      if (selectedUser) params.user_id = selectedUser;
      if (messageIdFilter) params.message_id = messageIdFilter;
      if (channelFilter) params.channel = channelFilter;

      const response = await reportsAPI.getSMSDetailed(params);
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setTotal(response.data.total || 0);
      } else {
        setData([]);
        setTotal(0);
      }
      setPage(p);
      setPageSize(ps);
      setFiltersOpen(false);
    } catch (err) {
      showToast('error', 'Failed to fetch detailed reports');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (v, fallback) => {
    const d = v || fallback;
    return d ? dayjs(d).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'DELIVRD' || s === 'DELIVERED') return { bg: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: 'rgba(16, 185, 129, 0.2)', icon: CheckCircle };
    if (s === 'SUBMITTED' || s === 'SENT') return { bg: 'rgba(99, 102, 241, 0.1)', color: '#4f46e5', border: 'rgba(99, 102, 241, 0.2)', icon: CheckCircle };
    if (s === 'FAILED') return { bg: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.2)', icon: XCircle };
    if (s === 'REJECTD' || s === 'REJECTED') return { bg: 'rgba(190, 18, 60, 0.1)', color: '#be123c', border: 'rgba(190, 18, 60, 0.2)', icon: XCircle };
    if (s === 'PENDING' || s === 'ACCEPTED') return { bg: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: 'rgba(245, 158, 11, 0.2)', icon: Clock };
    if (s === 'EXPIRED') return { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: 'rgba(107, 114, 128, 0.2)', icon: AlertCircle };
    if (s === 'DELETED') return { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: 'rgba(107, 114, 128, 0.2)', icon: XCircle };
    if (s === 'UNDELIV' || s === 'UNDELIVERED') return { bg: 'rgba(249, 115, 22, 0.1)', color: '#ea580c', border: 'rgba(249, 115, 22, 0.2)', icon: AlertCircle };
    return { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: 'rgba(107, 114, 128, 0.2)', icon: Clock };
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.id === userId);
    return u ? u.username : (userId || '-');
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startRecord = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, total);

  const activeFilterCount = [mobileNumber, headerFilter, statusFilter, selectedUser, messageIdFilter, channelFilter].filter(v => v && String(v).trim() !== '').length;

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // CSV Export
  const handleExportCSV = () => {
    if (data.length === 0) {
      showToast('error', 'No data to export');
      return;
    }
    const headers = ['User', 'Date', 'Queue Time', 'Delivery Date', 'Message ID', 'Header', 'Mobile', 'Message', 'Parts', 'Status', 'Channel', 'Delivery Type', 'Entity ID', 'Template ID', 'Campaign ID'];
    const csvContent = [
      headers.join(','),
      ...data.map(r => [
        `"${getUserName(r.user_id)}"`,
        `"${r.created_at ? dayjs(r.created_at).format('DD-MM-YYYY HH:mm:ss') : '-'}"`,
        `"${r.submit_attempted_at ? dayjs(r.submit_attempted_at).format('DD-MM-YYYY HH:mm:ss') : '-'}"`,
        `"${r.dlr_received_at ? dayjs(r.dlr_received_at).format('DD-MM-YYYY HH:mm:ss') : '-'}"`,
        r.message_id || '',
        r.header || '',
        r.mobile_number || '',
        `"${(r.message_content || '').replace(/"/g, '""')}"`,
        r.sms_count || 1,
        r.status || '',
        r.channel || '',
        r.delivery_type || '',
        r.entity_id ? `"\`${r.entity_id}"` : '',
        r.template_id ? `"\`${r.template_id}"` : '',
        r.campaign_id || ''
      ].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `detailed_report_${dayjs().format('YYYY-MM-DD_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', `Exported ${data.length} records to CSV`);
  };

  return (
    <div className="space-y-5">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[60] animate-slide-in">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium" style={{
            background: toast.type === 'success' ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #dc2626, #ef4444)',
            minWidth: '280px'
          }}>
            {toast.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            <span className="flex-1">{toast.message}</span>
            <button onClick={() => setToast({ show: false, type: '', message: '' })} className="ml-2 hover:opacity-80"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <FileText className="w-5 h-5 text-white" />
            </div>
            Detailed Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">Search and view individual SMS delivery records</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
            style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}
          >
            <Download className="w-3.5 h-3.5" />
            Export
          </button>
          <button
            onClick={() => fetchData(page, pageSize)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
            style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => fetchData(1, pageSize)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
          >
            <Search className="w-3.5 h-3.5" />
            Search
          </button>
        </div>
      </div>

      {/* Collapsible Filter Panel */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
          style={{ background: 'rgba(99, 102, 241, 0.02)' }}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#6366f1' }} />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {filtersOpen && (
          <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 p-4">
              {/* User Select */}
              <div className="min-w-0" style={{ position: 'relative', zIndex: showUserDropdown ? 50 : 1 }}>
                <label className={labelClass}>User</label>
                <div className="relative">
                  <input
                    type="text"
                    className={inputClass}
                    style={inputBorder}
                    placeholder="All Users"
                    value={selectedUser ? getUserName(selectedUser) : userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setSelectedUser('');
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                  />
                  {selectedUser && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedUser(''); setUserSearch(''); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {showUserDropdown && filteredUsers.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-xl max-h-60 overflow-y-auto" style={{ border: '1px solid rgba(99, 102, 241, 0.15)', zIndex: 9999 }}>
                    {filteredUsers.slice(0, 30).map(u => (
                      <button
                        key={u.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedUser(u.id);
                          setUserSearch('');
                          setShowUserDropdown(false);
                        }}
                      >
                        <span className="font-medium text-gray-800">{u.username}</span>
                        <span className="text-[10px] text-gray-400 ml-2">ID: {u.id}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* From Date */}
              <div className="min-w-0">
                <label className={labelClass}>From Date</label>
                <input type="date" className={inputClass} style={inputBorder} value={fromDate} max={toDate || dayjs().format('YYYY-MM-DD')} min={dayjs().subtract(7, 'days').format('YYYY-MM-DD')} onChange={e => setFromDate(e.target.value)} />
              </div>

              {/* To Date */}
              <div className="min-w-0">
                <label className={labelClass}>To Date</label>
                <input type="date" className={inputClass} style={inputBorder} value={toDate} max={dayjs().format('YYYY-MM-DD')} min={fromDate || dayjs().subtract(7, 'days').format('YYYY-MM-DD')} onChange={e => setToDate(e.target.value)} />
              </div>

              {/* Mobile */}
              <div className="min-w-0">
                <label className={labelClass}>Mobile</label>
                <input
                  type="text"
                  className={inputClass}
                  style={inputBorder}
                  placeholder="Mobile number"
                  value={mobileNumber}
                  onChange={e => setMobileNumber(e.target.value)}
                />
              </div>

              {/* Header */}
              <div className="min-w-0">
                <label className={labelClass}>Header</label>
                <input
                  type="text"
                  className={inputClass}
                  style={inputBorder}
                  placeholder="Sender ID"
                  value={headerFilter}
                  onChange={e => setHeaderFilter(e.target.value)}
                />
              </div>

              {/* Message ID */}
              <div className="min-w-0">
                <label className={labelClass}>Message ID</label>
                <input
                  type="text"
                  className={inputClass}
                  style={inputBorder}
                  placeholder="Message ID"
                  value={messageIdFilter}
                  onChange={e => setMessageIdFilter(e.target.value)}
                />
              </div>

              {/* Channel */}
              <div className="min-w-0">
                <label className={labelClass}>Channel</label>
                <select
                  className={inputClass}
                  style={inputBorder}
                  value={channelFilter}
                  onChange={e => setChannelFilter(e.target.value)}
                >
                  <option value="">All Channels</option>
                  <option value="GUI">GUI</option>
                  <option value="HTTP">HTTP</option>
                  <option value="SMPP">SMPP</option>
                </select>
              </div>

              {/* Status */}
              <div className="min-w-0">
                <label className={labelClass}>Status</label>
                <select
                  className={inputClass}
                  style={inputBorder}
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="UNDELIVERED">Undelivered</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Table */}
      {loading ? (
        <div className="bg-white rounded-xl p-16 flex flex-col items-center" style={cardStyle}>
          <RefreshCw className="w-8 h-8 animate-spin mb-3" style={{ color: '#6366f1' }} />
          <p className="text-sm text-gray-500">Loading reports...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
          {/* Table Header Info */}
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: '#6366f1' }} />
              <span className="text-sm font-semibold text-gray-700">Results</span>
              {total > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>
                  {startRecord}–{endRecord} of {formatIndianNumber(total)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
              {/* Page size */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase">Rows</span>
                <select
                  className="h-7 px-1.5 text-xs rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
                  value={pageSize}
                  onChange={e => { const ps = parseInt(e.target.value); setPageSize(ps); fetchData(1, ps); }}
                >
                  {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Queue Time</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Delivery Date</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Message ID</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Header</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-3 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Parts</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Delivery Type</th>
                  <th className="px-3 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-8 h-8 text-gray-300" />
                        <p className="text-sm text-gray-400 font-medium">No records found</p>
                        <p className="text-xs text-gray-300">Select date range and click Search</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => {
                    const st = getStatusBadge(row.status);
                    const StIcon = st.icon;
                    return (
                      <tr key={row.id || index} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                        <td className="px-3 py-3 text-xs text-gray-400 font-medium">
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs font-semibold text-gray-800">{getUserName(row.user_id)}</span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {row.created_at ? dayjs(row.created_at).format('DD-MM-YYYY HH:mm:ss') : '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {row.submit_attempted_at ? dayjs(row.submit_attempted_at).format('DD-MM-YYYY HH:mm:ss') : '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {row.dlr_received_at ? dayjs(row.dlr_received_at).format('DD-MM-YYYY HH:mm:ss') : '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-[11px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(99, 102, 241, 0.05)', color: '#4f46e5' }}>
                            {row.message_id || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs font-bold text-gray-800">{row.header || '-'}</span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs font-mono text-gray-700">
                          {row.mobile_number || '-'}
                        </td>
                        <td className="px-3 py-3 max-w-[180px]">
                          <button
                            className="text-left text-xs text-indigo-600 hover:text-indigo-800 truncate block w-full transition-colors"
                            onClick={() => { setSelectedMsg(row); setModalVisible(true); }}
                            title={row.message_content}
                          >
                            {row.message_content ? (row.message_content.length > 35 ? row.message_content.substring(0, 35) + '...' : row.message_content) : '-'}
                          </button>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-xs font-semibold text-gray-600">{row.sms_count || 1}</span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                            <StIcon className="w-3 h-3" />
                            {row.status || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(139, 92, 246, 0.08)', color: '#7c3aed' }}>{row.delivery_type || '-'}</span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center">
                          <button
                            onClick={() => { setSelectedMsg(row); setModalVisible(true); }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm"
                            style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.1)' }}
                          >
                            <Eye className="w-3 h-3" /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {total > 0 && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="text-xs text-gray-500">
                Showing {startRecord} to {endRecord} of {formatIndianNumber(total)} records
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => fetchData(page - 1, pageSize)}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg transition-all disabled:opacity-30"
                  style={{ color: '#4f46e5', background: page > 1 ? 'rgba(99, 102, 241, 0.06)' : 'transparent' }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {getPageNumbers().map((pg, idx) =>
                  pg === '...' ? (
                    <span key={`e-${idx}`} className="px-2 text-xs text-gray-400">...</span>
                  ) : (
                    <button
                      key={pg}
                      onClick={() => fetchData(pg, pageSize)}
                      className="min-w-[28px] h-7 rounded-lg text-xs font-semibold transition-all"
                      style={pg === page
                        ? { background: 'linear-gradient(135deg, #4f46e5, #06b6d4)', color: '#fff' }
                        : { color: '#4f46e5', background: 'rgba(99, 102, 241, 0.04)' }
                      }
                    >
                      {pg}
                    </button>
                  )
                )}
                <button
                  onClick={() => fetchData(page + 1, pageSize)}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg transition-all disabled:opacity-30"
                  style={{ color: '#4f46e5', background: page < totalPages ? 'rgba(99, 102, 241, 0.06)' : 'transparent' }}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SMS Details Modal — Enterprise Design */}
      {modalVisible && selectedMsg && (() => {
        const st = getStatusBadge(selectedMsg.status);
        const StIcon = st.icon;
        const statusUpper = (selectedMsg.status || '').toUpperCase();
        const isDelivered = ['DELIVERED', 'DELIVRD'].includes(statusUpper);
        const isPending = ['PENDING', 'ACCEPTED', 'SUBMITTED', 'SENT'].includes(statusUpper);
        const hasError = !isDelivered && !isPending;
        const copyToClipboard = (text) => {
          navigator.clipboard.writeText(text).then(() => showToast('success', 'Copied to clipboard'));
        };
        const headerBg = isDelivered ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
          : hasError ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
          : 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)';
        return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 pt-10 sm:pt-0 sm:p-4" onClick={() => { setModalVisible(false); setSelectedMsg(null); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] sm:max-h-[92vh] overflow-hidden flex flex-col" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }} onClick={e => e.stopPropagation()}>

            {/* ── Header — color-coded by status ── */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 sm:py-4 flex items-center justify-between" style={{ background: headerBg }}>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">SMS Details</h3>
                  <p className="text-[10px] sm:text-[11px] text-white/70 font-medium truncate">Full message delivery information</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-extrabold bg-white/20 backdrop-blur text-white border border-white/30 shadow-sm">
                  <StIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {selectedMsg.status || 'UNKNOWN'}
                </span>
                <button onClick={() => { setModalVisible(false); setSelectedMsg(null); }} className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors">
                  <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                </button>
              </div>
            </div>

            {/* ── Body ── */}
            <div className="flex-1 overflow-y-auto">

              {/* ── Delivery Process Flow: Date → Queue Time → Delivery Date ── */}
              <div className="px-4 sm:px-6 py-4 sm:py-5" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.03), rgba(6, 182, 212, 0.03))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  {/* Step 1: Date (created_at) */}
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1.5 sm:mb-2" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)', boxShadow: '0 3px 10px rgba(79, 70, 229, 0.3)' }}>
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="text-[10px] sm:text-xs font-bold text-indigo-700 uppercase tracking-wide">Date</div>
                    <div className="text-[10px] sm:text-xs text-gray-600 font-semibold mt-0.5 text-center">{selectedMsg.created_at ? dayjs(selectedMsg.created_at).format('DD-MM-YYYY HH:mm:ss') : '—'}</div>
                  </div>
                  {/* Arrow 1 */}
                  <div className="flex items-center -mt-5 sm:-mt-6">
                    <div className="w-4 sm:w-8 h-0.5 rounded-full" style={{ background: selectedMsg.submit_attempted_at ? '#4f46e5' : '#d1d5db' }} />
                    <div className="text-base sm:text-lg font-bold -ml-1" style={{ color: selectedMsg.submit_attempted_at ? '#4f46e5' : '#d1d5db' }}>›</div>
                    <div className="text-base sm:text-lg font-bold -ml-1.5" style={{ color: selectedMsg.submit_attempted_at ? '#4f46e5' : '#d1d5db' }}>›</div>
                  </div>
                  {/* Step 2: Queue Time (submit_attempted_at) */}
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1.5 sm:mb-2" style={{
                      background: selectedMsg.submit_attempted_at ? 'linear-gradient(135deg, #f59e0b, #fbbf24)' : 'linear-gradient(135deg, #9ca3af, #d1d5db)',
                      boxShadow: selectedMsg.submit_attempted_at ? '0 3px 10px rgba(245, 158, 11, 0.3)' : '0 3px 10px rgba(156, 163, 175, 0.2)'
                    }}>
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="text-[10px] sm:text-xs font-bold uppercase tracking-wide" style={{ color: selectedMsg.submit_attempted_at ? '#b45309' : '#9ca3af' }}>Queue Time</div>
                    <div className="text-[10px] sm:text-xs text-gray-600 font-semibold mt-0.5 text-center">{selectedMsg.submit_attempted_at ? dayjs(selectedMsg.submit_attempted_at).format('DD-MM-YYYY HH:mm:ss') : '—'}</div>
                  </div>
                  {/* Arrow 2 */}
                  <div className="flex items-center -mt-5 sm:-mt-6">
                    <div className="w-4 sm:w-8 h-0.5 rounded-full" style={{ background: isDelivered ? '#10b981' : hasError ? '#ef4444' : '#d1d5db' }} />
                    <div className="text-base sm:text-lg font-bold -ml-1" style={{ color: isDelivered ? '#10b981' : hasError ? '#ef4444' : '#d1d5db' }}>›</div>
                    <div className="text-base sm:text-lg font-bold -ml-1.5" style={{ color: isDelivered ? '#10b981' : hasError ? '#ef4444' : '#d1d5db' }}>›</div>
                  </div>
                  {/* Step 3: Delivery Date (dlr_received_at) */}
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1.5 sm:mb-2" style={{
                      background: isDelivered ? 'linear-gradient(135deg, #059669, #10b981)' : hasError ? 'linear-gradient(135deg, #dc2626, #ef4444)' : 'linear-gradient(135deg, #9ca3af, #d1d5db)',
                      boxShadow: isDelivered ? '0 3px 10px rgba(16, 185, 129, 0.3)' : hasError ? '0 3px 10px rgba(239, 68, 68, 0.3)' : '0 3px 10px rgba(156, 163, 175, 0.3)'
                    }}>
                      {isDelivered ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> : hasError ? <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> : <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />}
                    </div>
                    <div className={`text-[10px] sm:text-xs font-bold uppercase tracking-wide ${isDelivered ? 'text-emerald-700' : hasError ? 'text-red-700' : 'text-gray-400'}`}>
                      Delivery Date
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-600 font-semibold mt-0.5 text-center">
                      {selectedMsg.dlr_received_at ? dayjs(selectedMsg.dlr_received_at).format('DD-MM-YYYY HH:mm:ss') : '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5">

                {/* ── Section: Delivery Overview ── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #4f46e5, #06b6d4)' }} />
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">Delivery Overview</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {/* Mobile */}
                    <div className="rounded-xl p-3 sm:p-3.5 group hover:shadow-sm transition-shadow flex sm:block items-center justify-between" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <div className="flex items-center gap-1.5 sm:mb-1.5">
                        <Phone className="w-3.5 h-3.5 text-indigo-500" />
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Mobile Number</span>
                      </div>
                      <div className="text-sm sm:text-base font-mono font-bold text-gray-900 tracking-wide">{selectedMsg.mobile_number || '—'}</div>
                    </div>
                    {/* Header */}
                    <div className="rounded-xl p-3 sm:p-3.5 flex sm:block items-center justify-between" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <div className="flex items-center gap-1.5 sm:mb-1.5">
                        <Hash className="w-3.5 h-3.5 text-indigo-500" />
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Header / Sender ID</span>
                      </div>
                      <div className="text-sm sm:text-base font-bold text-gray-900">{selectedMsg.header || selectedMsg.header_id || '—'}</div>
                    </div>
                    {/* SMS Parts */}
                    <div className="rounded-xl p-3 sm:p-3.5 flex sm:block items-center justify-between" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <div className="flex items-center gap-1.5 sm:mb-1.5">
                        <MessageSquare className="w-3.5 h-3.5 text-indigo-500" />
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">SMS Parts</span>
                      </div>
                      <div className="text-sm sm:text-base font-bold text-gray-900">
                        {selectedMsg.sms_count || 1}
                        {selectedMsg.sms_count > 1 && <span className="ml-1.5 text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Multipart</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Section: Routing & Channel ── */}
                {(selectedMsg.user_id || selectedMsg.channel || selectedMsg.delivery_type || selectedMsg.entity_name || selectedMsg.entity_id) && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #8b5cf6, #a78bfa)' }} />
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">Routing & Channel</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selectedMsg.user_id && (
                      <div className="rounded-xl p-3 sm:p-3.5" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <User className="w-3.5 h-3.5 text-violet-500" />
                          <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider">User</span>
                        </div>
                        <div className="text-sm sm:text-base font-semibold text-gray-900">{getUserName(selectedMsg.user_id)}</div>
                      </div>
                    )}
                    {selectedMsg.channel && (
                      <div className="rounded-xl p-3 sm:p-3.5" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                        <div className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Channel</div>
                        <span className="inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md text-xs sm:text-sm font-bold" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#7c3aed' }}>
                          {selectedMsg.channel}
                        </span>
                      </div>
                    )}
                    {selectedMsg.delivery_type && (
                      <div className="rounded-xl p-3 sm:p-3.5" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                        <div className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Delivery Type</div>
                        <span className="inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md text-xs sm:text-sm font-bold" style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#0891b2' }}>
                          {selectedMsg.delivery_type}
                        </span>
                      </div>
                    )}
                    {selectedMsg.entity_name && (
                      <div className="rounded-xl p-3 sm:p-3.5" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                        <div className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Entity Name</div>
                        <div className="text-xs sm:text-base font-semibold text-gray-900 break-words">{selectedMsg.entity_name}</div>
                      </div>
                    )}
                    {selectedMsg.entity_id && (
                      <div className="rounded-xl p-3 sm:p-3.5" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                        <div className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Entity ID</div>
                        <div className="text-xs sm:text-sm font-mono font-semibold text-gray-900 break-all">{selectedMsg.entity_id}</div>
                      </div>
                    )}
                    {selectedMsg.cost && (
                      <div className="rounded-xl p-3 sm:p-3.5" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                        <div className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost</div>
                        <div className="text-sm sm:text-base font-bold text-gray-900">{`₹${selectedMsg.cost}`}</div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* ── Section: Identifiers ── */}
                {(selectedMsg.message_id || selectedMsg.template_id || selectedMsg.campaign_id) && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #0891b2, #22d3ee)' }} />
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">Identifiers</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      ...(selectedMsg.message_id ? [{ label: 'Message ID', value: selectedMsg.message_id }] : []),
                      ...(selectedMsg.template_id ? [{ label: 'Template ID', value: selectedMsg.template_id }] : []),
                      ...(selectedMsg.campaign_id ? [{ label: 'Campaign ID', value: selectedMsg.campaign_id }] : []),
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl px-4 py-3 group" style={{ background: 'rgba(8, 145, 178, 0.04)', border: '1px solid rgba(8, 145, 178, 0.1)' }}>
                        <div className="min-w-0 flex-1">
                          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{item.label}</span>
                          <div className="text-sm font-mono font-semibold text-gray-900 break-all mt-0.5 select-all">{item.value}</div>
                        </div>
                        <button
                          onClick={() => copyToClipboard(item.value)}
                          className="ml-3 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-cyan-50"
                          title="Copy"
                        >
                          <svg className="w-4 h-4 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* ── Section: Error Information (only for non-delivered) ── */}
                {hasError && selectedMsg.error_code && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #dc2626, #f87171)' }} />
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">Error Information</span>
                  </div>
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                    <div className="px-4 py-3.5 flex items-center justify-between" style={{ background: 'rgba(239, 68, 68, 0.05)', borderBottom: '1px solid rgba(239, 68, 68, 0.1)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Error Code</div>
                          <span className="font-mono text-lg font-bold text-red-700">{selectedMsg.error_code}</span>
                        </div>
                      </div>
                      {selectedMsg.error_status_type && (
                        <span className="px-3 py-1.5 rounded-full text-xs font-bold" style={{
                          background: selectedMsg.error_status_type === 'REJECTED' ? 'rgba(239, 68, 68, 0.1)' :
                                     selectedMsg.error_status_type === 'UNDELIVERED' ? 'rgba(245, 158, 11, 0.1)' :
                                     selectedMsg.error_status_type === 'NDNC' ? 'rgba(124, 58, 237, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                          color: selectedMsg.error_status_type === 'REJECTED' ? '#dc2626' :
                                 selectedMsg.error_status_type === 'UNDELIVERED' ? '#d97706' :
                                 selectedMsg.error_status_type === 'NDNC' ? '#7c3aed' : '#6b7280',
                          border: `1px solid ${selectedMsg.error_status_type === 'REJECTED' ? 'rgba(239, 68, 68, 0.2)' :
                                 selectedMsg.error_status_type === 'UNDELIVERED' ? 'rgba(245, 158, 11, 0.2)' :
                                 selectedMsg.error_status_type === 'NDNC' ? 'rgba(124, 58, 237, 0.2)' : 'rgba(107, 114, 128, 0.2)'}`
                        }}>
                          {selectedMsg.error_status_type}
                        </span>
                      )}
                    </div>
                    {selectedMsg.error_description && (
                      <div className="px-4 py-3.5" style={{ background: 'rgba(239, 68, 68, 0.02)' }}>
                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Description</div>
                        <div className="text-base text-gray-900 leading-relaxed font-medium">{selectedMsg.error_description}</div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* ── Section: Remarks (Master Admin) ── */}
                {selectedMsg.remarks && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #f59e0b, #fbbf24)' }} />
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">Remarks</span>
                  </div>
                  <div className="rounded-xl p-4" style={{ background: 'rgba(245, 158, 11, 0.04)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                    <div className="text-sm text-gray-800 font-medium leading-relaxed break-words whitespace-pre-wrap">{selectedMsg.remarks}</div>
                  </div>
                </div>
                )}

                {/* ── Section: Message Content ── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #059669, #34d399)' }} />
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">Message Content</span>
                    {selectedMsg.message_content && (
                      <span className="text-[11px] text-gray-500 font-semibold ml-auto">{selectedMsg.message_content.length} chars</span>
                    )}
                  </div>
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(5, 150, 105, 0.12)' }}>
                    <div className="p-4 text-sm text-gray-800 whitespace-pre-wrap break-all font-mono leading-relaxed" style={{ background: 'rgba(5, 150, 105, 0.02)', minHeight: '60px', maxHeight: '220px', overflow: 'auto' }}>
                      {selectedMsg.message_content || '—'}
                    </div>
                    {selectedMsg.message_content && (
                      <div className="flex justify-end px-3 py-1.5" style={{ borderTop: '1px solid rgba(5, 150, 105, 0.08)', background: 'rgba(5, 150, 105, 0.03)' }}>
                        <button
                          onClick={() => copyToClipboard(selectedMsg.message_content)}
                          className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 hover:text-emerald-700 transition-colors px-2 py-1 rounded-md hover:bg-emerald-50"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                          Copy Message
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* ── Footer ── */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.1)', background: 'rgba(99, 102, 241, 0.02)' }}>
              <div className="text-[10px] text-gray-400 font-medium">
                ID: <span className="font-mono">{selectedMsg.id || '—'}</span>
              </div>
              <button
                onClick={() => { setModalVisible(false); setSelectedMsg(null); }}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-lg active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default ReportsDetailed;
